import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiCheck, FiMinus } from "react-icons/fi";
import api, { formatCents } from "../services/api";

const COMPARE_ROWS = [
  { feature: "Users", values: ["Up to 2", "Up to 5", "Unlimited"] },
  { feature: "Companies", values: ["1", "Up to 4", "Unlimited"] },
  { feature: "Invoices", values: ["Unlimited", "Unlimited", "Unlimited"] },
  { feature: "Expense Tracking", values: [true, true, true] },
  { feature: "Bank Reconciliation", values: [true, true, true] },
  { feature: "Advanced Reports", values: [false, true, true] },
  { feature: "Payroll & 1099", values: [false, true, true] },
  { feature: "Custom Roles", values: [false, false, true] },
  { feature: "API Access", values: [false, false, true] },
  { feature: "Priority Support", values: [false, true, true] },
];

function CompareValue({ value }) {
  if (value === true)
    return <FiCheck className="mx-auto text-emerald-500" size={16} />;
  if (value === false)
    return <FiMinus className="mx-auto text-slate-300" size={16} />;
  return <span className="text-sm text-slate-600">{value}</span>;
}

export default function Pricing() {
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    let mounted = true;
    api.getPlans().then((res) => {
      if (mounted) setPlans(res.plans);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <>
      <section className="section-container py-16 text-center">
        <h1 className="text-3xl font-bold sm:text-4xl">
          Simple, Transparent Pricing
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-slate-600">
          Choose the plan that fits your business. Every plan is billed once a
          year.
        </p>
      </section>

      {/* Plan cards */}
      <section className="section-container pb-16">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {plans.map((plan) => {
            const priceCents = plan.priceCents;
            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-xl bg-white p-7 ${
                  plan.popular
                    ? "border-2 border-primary shadow-lifted"
                    : "border border-line shadow-card"
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-xs font-semibold text-white">
                    Most Popular
                  </span>
                )}
                <h3 className="text-lg font-semibold">{plan.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-navy">
                    {formatCents(priceCents)}
                  </span>
                  <span className="text-sm text-slate-500">/ year</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Billed yearly — renews automatically
                </p>
                <p className="mt-3 text-sm text-slate-600">
                  {plan.description}
                </p>
                <Link
                  to={`/register?plan=${plan.id}`}
                  className={`mt-6 ${plan.popular ? "btn-primary" : "btn-outline"}`}
                >
                  Get Started
                </Link>
                <ul className="mt-6 space-y-2.5 border-t border-line pt-6">
                  {(plan.features || []).map((f) => (
                    <li
                      key={f}
                      className="flex items-center gap-2 text-sm text-slate-600"
                    >
                      <FiCheck
                        className="shrink-0 text-emerald-500"
                        size={15}
                      />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Compare table */}
      <section className="section-container pb-16">
        <h2 className="text-center text-2xl font-bold">Compare All Features</h2>
        <div className="card mt-8 overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead>
              <tr className="border-b border-line bg-surface/70">
                <th className="px-6 py-4 text-sm font-semibold text-navy">
                  Features
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-navy">
                  Starter
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-primary">
                  Professional
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-navy">
                  Business
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row) => (
                <tr
                  key={row.feature}
                  className="border-b border-line last:border-0"
                >
                  <td className="px-6 py-3.5 text-sm font-medium text-navy">
                    {row.feature}
                  </td>
                  {row.values.map((v, i) => (
                    <td key={i} className="px-6 py-3.5 text-center">
                      <CompareValue value={v} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Contact strip */}
      <section className="section-container pb-16">
        <div className="flex flex-col items-center justify-between gap-5 rounded-2xl bg-navy px-6 py-8 text-center sm:flex-row sm:px-10 sm:text-left">
          <p className="text-sm font-medium text-white sm:text-base">
            Still not sure? Contact us and we'll help you choose the right plan.
          </p>
          <Link to="/contact" className="btn-primary shrink-0">
            Contact Sales
          </Link>
        </div>
      </section>
    </>
  );
}
